import { useEffect, useState } from 'react';

import { fetchContent } from '../api/client';
import type { SiteContent } from '../api/types';
import { fallbackContent } from '../content/fallback';

export type ContentSource = 'fallback' | 'api';

interface ContentState {
  content: SiteContent;
  source: ContentSource;
}

/**
 * Renders from the bundled copy straight away and upgrades to the API response
 * when it arrives. There is deliberately no loading state: the page is never
 * blank, and a failed fetch is a non-event for the visitor.
 */
export function useSiteContent(): ContentState {
  const [state, setState] = useState<ContentState>(() => ({
    content: fallbackContent,
    source: 'fallback',
  }));

  useEffect(() => {
    const controller = new AbortController();

    fetchContent(controller.signal)
      .then((content) => setState({ content, source: 'api' }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.warn('Falling back to bundled content:', error);
      });

    return () => controller.abort();
  }, []);

  return state;
}
